import * as Sentry from "@sentry/react";
import { SupabaseClient } from "@supabase/supabase-js";
import { supabaseIntegration } from "@supabase/sentry-js-integration";
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
  } from "react-router-dom";
import { useEffect } from "react";


export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Only initialize Sentry if DSN is provided
  if (!dsn || dsn.trim() === '') {
    console.log("Sentry not initialized - no DSN provided");
    return;
  }

  const client = Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration({
        // Enable automatic instrumentation of user interactions
        instrumentPageLoad: true,
        instrumentNavigation: true,
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      supabaseIntegration(SupabaseClient, Sentry, {
        tracing: true,
        breadcrumbs: true,
        errors: true,
      }),
    ],
    enabled: true,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "staging",
    _experiments: { enableLogs: true },
    tracesSampleRate: 1.0,
    // include supabase edge functions in trace propagation
    tracePropagationTargets: [/https:\/\/.*\.supabase\.co\/functions\/v1/],
    ignoreErrors: [
      /Object Not Found Matching Id/,
      /MethodName:\w+/,
      /ParamCount:\d+/,
      /ResizeObserver loop/,
      /Script error/,
      /Non-Error promise rejection captured/,
      /chrome-extension:/,
      /moz-extension:/,
      /safari-extension:/,
      /Cannot close a closed AudioContext/,
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;

      if (typeof error === 'string') {
        if (error.includes('Object Not Found Matching Id') ||
            error.includes('MethodName:') ||
            error.includes('ParamCount:')) {
          return null;
        }
      }

      if (error instanceof Error) {
        if (error.message?.includes('Object Not Found Matching Id') ||
            error.message?.includes('MethodName:') ||
            error.message?.includes('ParamCount:')) {
          return null;
        }
      }

      if (event.exception?.values?.[0]?.stacktrace?.frames) {
        const frames = event.exception.values[0].stacktrace.frames;
        const hasBrowserExtension = frames.some(frame =>
          frame.filename?.includes('chrome-extension://') ||
          frame.filename?.includes('moz-extension://') ||
          frame.filename?.includes('safari-extension://')
        );
        if (hasBrowserExtension) {
          return null;
        }
      }

      return event;
    },
  });
  console.log("Sentry initialized", client.getOptions());
}
